import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaUserCircle, FaInstagram, FaChevronRight, FaLink } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import { HiSparkles } from 'react-icons/hi';
import Styles from './styles.module.css';
import { ABOUT_ME_EXTRA_FIELDS, growthLogCategoryLabel, styleTagLabel } from './constants';

type PublicMyCard = {
  uid: string;
  name: string;
  text: string;
  instagram: string;
  tiktok: string;
  imageUrl: string;
  education: { name: string; grade: string };
  graduation: { year: number | null; month: number | null };
  styleTagIds: string[];
  aboutMe: {
    idealBeautician: string;
    reasonForCareer: string;
    customerServiceValue: string;
    favoriteStyle: string;
    currentEffort: string;
    futureGoal: string;
    salonPreference: string;
  };
  pickupWorks: { id: string; label: string; imageUrl: string }[];
  growthLogs: { id: string; title: string; category: string; activityDate: string | null; imageUrl: string | null }[];
};

type LoadState = 'loading' | 'private' | 'ready';

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const StudentCardPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>('loading');
  const [card, setCard] = useState<PublicMyCard | null>(null);
  const [aboutMeExpanded, setAboutMeExpanded] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    setState('loading');
    setCard(null);
    setAboutMeExpanded(false);
    setAvatarError(false);

    fetch(`/student/api/${encodeURIComponent(slug)}`)
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        if (json && json.public && json.card) {
          setCard(json.card as PublicMyCard);
          setState('ready');
        } else {
          setState('private');
        }
      })
      .catch(() => {
        if (!cancelled) setState('private');
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state === 'loading') {
    return <div className={Styles.page} />;
  }

  if (state === 'private' || !card) {
    return (
      <div className={Styles.privatePage}>
        <div className={Styles.privateCard}>
          <p className={Styles.privateText}>このMy Cardは非公開です</p>
        </div>
        <p className={Styles.brand}>memorii works</p>
      </div>
    );
  }

  const schoolLine = [card.education.name, card.education.grade].filter((v) => v.trim() !== '').join(' ');
  const graduationLabel = card.graduation.year
    ? `${card.graduation.year}年${card.graduation.month ? `${card.graduation.month}月` : ''}卒業予定`
    : '';

  const aboutMeExtras = ABOUT_ME_EXTRA_FIELDS.filter(({ key }) => card.aboutMe[key].trim() !== '');
  const hasAboutMe = card.aboutMe.idealBeautician.trim() !== '' || aboutMeExtras.length > 0;
  const hasSns = card.instagram.trim() !== '' || card.tiktok.trim() !== '';

  return (
    <div className={Styles.page}>
      <div className={Styles.container}>
        {/* ヘッダー */}
        <div className={Styles.card}>
          <div className={Styles.headerRow}>
            {avatarError ? (
              <div className={Styles.avatarFallback}>
                <FaUserCircle size={36} color="#B99B72" />
              </div>
            ) : (
              <img
                src={card.imageUrl}
                alt={card.name || '美容学生'}
                className={Styles.avatar}
                onError={() => setAvatarError(true)}
              />
            )}
            <div className={Styles.headerInfo}>
              <p className={Styles.name}>{card.name || '名前未設定'}</p>
              {schoolLine && <p className={Styles.schoolLine}>{schoolLine}</p>}
              {graduationLabel && <p className={Styles.graduationLabel}>{graduationLabel}</p>}
              {card.text && <p className={Styles.bio}>{card.text}</p>}
            </div>
          </div>

          {card.styleTagIds.length > 0 && (
            <div className={Styles.chipRow}>
              {card.styleTagIds.slice(0, 3).map((id) => (
                <span key={id} className={Styles.chip}>
                  #{styleTagLabel(id)}
                </span>
              ))}
            </div>
          )}

          {card.instagram && (
            <a
              className={Styles.instagramButton}
              href={`https://www.instagram.com/${card.instagram}/`}
              target="_blank"
              rel="noreferrer"
            >
              <FaInstagram size={15} />
              Instagramを見る
            </a>
          )}
        </div>

        {/* About Me */}
        {hasAboutMe && (
          <section className={Styles.section}>
            <h2 className={Styles.sectionHeading}>About Me</h2>
            <div className={Styles.card}>
              {card.aboutMe.idealBeautician && <p className={Styles.idealText}>{card.aboutMe.idealBeautician}</p>}

              {aboutMeExtras.length > 0 && (
                <>
                  {aboutMeExpanded && (
                    <div className={Styles.aboutMeExtras}>
                      {aboutMeExtras.map(({ key, label }) => (
                        <div key={key} className={Styles.aboutMeExtraItem}>
                          <p className={Styles.aboutMeExtraLabel}>{label}</p>
                          <p className={Styles.aboutMeExtraValue}>{card.aboutMe[key]}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    className={Styles.expandButton}
                    onClick={() => setAboutMeExpanded((v) => !v)}
                  >
                    {aboutMeExpanded ? '閉じる' : 'もっと見る'}
                  </button>
                </>
              )}
            </div>
          </section>
        )}

        {/* Pick Up Works */}
        {card.pickupWorks.length > 0 && (
          <section className={Styles.section}>
            <h2 className={Styles.sectionHeading}>Pick Up Works</h2>
            <div className={Styles.worksGrid}>
              {card.pickupWorks.map((work) => (
                <PickupWorkCard key={work.id} work={work} />
              ))}
            </div>
          </section>
        )}

        {/* Growth Log */}
        {card.growthLogs.length > 0 && (
          <section className={Styles.section}>
            <h2 className={Styles.sectionHeading}>Growth Log</h2>
            <div className={Styles.timeline}>
              {card.growthLogs.map((log, index) => (
                <GrowthLogRow key={log.id} log={log} isLast={index === card.growthLogs.length - 1} />
              ))}
            </div>
          </section>
        )}

        {/* SNS / 外部リンク */}
        {hasSns && (
          <section className={Styles.section}>
            <h2 className={Styles.sectionHeadingSmall}>
              <FaLink size={13} className={Styles.sectionHeadingIcon} />
              SNS / 外部リンク
            </h2>
            <div className={Styles.card}>
              {card.instagram && (
                <a
                  className={Styles.linkRow}
                  href={`https://www.instagram.com/${card.instagram}/`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FaInstagram size={17} color="#8C6E4E" />
                  <span className={Styles.linkLabel}>Instagram</span>
                  <span className={Styles.linkSpacer} />
                  <span className={Styles.linkValue}>@{card.instagram}</span>
                  <FaChevronRight size={14} color="#B2A392" />
                </a>
              )}
              {card.instagram && card.tiktok && <div className={Styles.divider} />}
              {card.tiktok && (
                <a
                  className={Styles.linkRow}
                  href={`https://www.tiktok.com/@${card.tiktok}/`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <SiTiktok size={15} color="#8C6E4E" />
                  <span className={Styles.linkLabel}>TikTok</span>
                  <span className={Styles.linkSpacer} />
                  <span className={Styles.linkValue}>@{card.tiktok}</span>
                  <FaChevronRight size={14} color="#B2A392" />
                </a>
              )}
            </div>
          </section>
        )}

        <p className={Styles.brand}>memorii works</p>
      </div>
    </div>
  );
};

const PickupWorkCard: React.FC<{ work: { id: string; label: string; imageUrl: string } }> = ({ work }) => {
  const [error, setError] = useState(false);
  return (
    <div className={Styles.workCard}>
      {error ? (
        <div className={Styles.workImageFallback} />
      ) : (
        <img
          src={work.imageUrl}
          alt={work.label || 'Pick Up Work'}
          className={Styles.workImage}
          onError={() => setError(true)}
        />
      )}
      {work.label && <p className={Styles.workLabel}>{work.label}</p>}
    </div>
  );
};

const GrowthLogRow: React.FC<{
  log: { id: string; title: string; category: string; activityDate: string | null; imageUrl: string | null };
  isLast: boolean;
}> = ({ log, isLast }) => {
  const [error, setError] = useState(false);
  const showImage = log.imageUrl && !error;

  return (
    <div className={Styles.timelineRow}>
      <div className={Styles.timelineTrack}>
        <div className={Styles.timelineDot} />
        {!isLast && <div className={Styles.timelineLine} />}
      </div>
      <div className={Styles.timelineCard}>
        {showImage ? (
          <img src={log.imageUrl as string} alt={log.title} className={Styles.timelineImage} onError={() => setError(true)} />
        ) : (
          <div className={Styles.timelineImageFallback}>
            <HiSparkles size={18} color="#8C6E4E" />
          </div>
        )}
        <div className={Styles.timelineInfo}>
          <p className={Styles.timelineTitle}>{log.title}</p>
          <div className={Styles.timelineMetaRow}>
            <span className={Styles.timelineCategory}>{growthLogCategoryLabel(log.category)}</span>
            <span className={Styles.timelineDate}>{formatDate(log.activityDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentCardPage;
